import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { X } from 'lucide-react';

// Base UpdateComponent
const UpdateComponent = forwardRef(({ apiService, alertService, dataContext, getItemName, updateItem, renderForm }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const modalRef = useRef(null);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        openModal(item) {
            setSelectedItem({ ...item });
            setShowModal(true);
        },
        closeModal() {
            setSelectedItem(null);
            setShowModal(false);
        },
    }));

    // Handle clicks outside the modal
    const handleOverlayClick = (e) => {
        if (e.target === modalRef.current) {
            ref.current.closeModal();
        }
    };

    // Handle Escape key press
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            ref.current.closeModal();
        }
    };

    const handleUpdateItem = async (updatedItem) => {
        setIsLoading(true);
        try {
            await updateItem(updatedItem, dataContext, apiService);
            alertService.showAlert(`${getItemName()} updated successfully!`, 'success');
            ref.current.closeModal();
        } catch (error) {
            console.error(`Error updating ${getItemName()}:`, error);
            alertService.showAlert(`Failed to update ${getItemName()}: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (!showModal) return null;

    return (
        <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50"
            ref={modalRef}
            onClick={handleOverlayClick}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-labelledby={`update-${getItemName()}-title`}
            aria-modal="true"
            tabIndex={-1}
        >
            {renderForm({ selectedItem, setSelectedItem, handleUpdateItem, isLoading, closeModal: () => ref.current.closeModal() })}
        </div>
    );
});


// PatientUpdateComponent


const PatientUpdateComponent = forwardRef(({ apiService, alertService, patientContext }, ref) => {
  const getItemName = () => 'patient';

  const updateItem = async (updatedItem, dataContext, apiService) => {
    const mutation = `
      mutation UpdatePatient($patientId: String!, $name: String, $age: Int, $phone: String, $department: String, $symptoms: String, $medicalHistory: String) {
        updatePatient(patientId: $patientId, name: $name, age: $age, phone: $phone, department: $department, symptoms: $symptoms, medicalHistory: $medicalHistory) {
          id
          patientId
          name
          age
          phone
          department
          symptoms
          medicalHistory
          status
          organization {
            id
            name
          }
        }
      }
    `;
    const variables = {
      patientId: updatedItem.patientId || null,
      name: updatedItem.name || null,
      age: updatedItem.age ? parseInt(updatedItem.age, 10) : null,
      phone: updatedItem.phone || null,
      department: updatedItem.department || null,
      symptoms: updatedItem.symptoms || null,
      medicalHistory: updatedItem.medical_history || null,
    };

    const response = await apiService.graphqlRequest(mutation, variables);
    if (!response || !response.updatePatient || response.errors) {
      throw new Error(response.errors?.[0]?.message || 'Invalid response from server');
    }

    // Use dataContext.items and dataContext.setItems
    dataContext.setItems(
      dataContext.items.map((item) =>
        item.patientId === updatedItem.patientId ? response.updatePatient : item
      )
    );
  };

  const renderForm = ({ selectedItem, setSelectedItem, handleUpdateItem, isLoading, closeModal }) => {
    const validateForm = () => {
      if (!selectedItem?.name) {
        alertService.showAlert('Patient name is required', 'error');
        return false;
      }
      return true;
    };

    return (
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 id="update-patient-title" className="text-xl font-bold text-gray-900">Update Patient</h3>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Patient Information Display */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Patient Information:</h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div>
              <span className="font-medium text-gray-600">Patient ID:</span>
              <div className="text-gray-800">{selectedItem?.patientId || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Name:</span>
              <div className="text-gray-800">{selectedItem?.name || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Department:</span>
              <div className="text-gray-800">{selectedItem?.department || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Status:</span>
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                {selectedItem?.status || 'N/A'}
              </div>
              <p className="text-xs text-gray-500 mt-1">Status can be changed via the kebab menu in the patient list</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (validateForm()) handleUpdateItem(selectedItem);
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="update-patientId" className="block text-sm font-medium text-gray-700">Patient ID</label>
            <input
              type="text"
              id="update-patientId"
              value={selectedItem?.patientId || ''}
              readOnly
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
            />
          </div>

          <div>
            <label htmlFor="update-name" className="block text-sm font-medium text-gray-700">Name *</label>
            <input
              type="text"
              id="update-name"
              value={selectedItem?.name || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              required
              placeholder="Enter patient name"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="update-age" className="block text-sm font-medium text-gray-700">Age</label>
              <input
                type="number"
                id="update-age"
                value={selectedItem?.age || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, age: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter age"
                min="0"
                max="120"
              />
            </div>
            <div>
              <label htmlFor="update-phone" className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                id="update-phone"
                value={selectedItem?.phone || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, phone: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          <div>
            <label htmlFor="update-department" className="block text-sm font-medium text-gray-700">Department</label>
            <select
              id="update-department"
              value={selectedItem?.department || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, department: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Department</option>
              <option value="Cardiology">Cardiology</option>
              <option value="Orthopedics">Orthopedics</option>
              <option value="Emergency">Emergency</option>
              <option value="Pediatrics">Pediatrics</option>
              <option value="General Medicine">General Medicine</option>
            </select>
          </div>

          <div>
            <label htmlFor="update-symptoms" className="block text-sm font-medium text-gray-700">Symptoms</label>
            <textarea
              id="update-symptoms"
              value={selectedItem?.symptoms || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, symptoms: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
              placeholder="Describe symptoms..."
            />
          </div>

          <div>
            <label htmlFor="update-medicalHistory" className="block text-sm font-medium text-gray-700">Medical History</label>
            <textarea
              id="update-medicalHistory"
              value={selectedItem?.medical_history || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, medical_history: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              placeholder="Enter medical history..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={closeModal}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Saving...' : 'Update Patient'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <UpdateComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={patientContext}
      getItemName={getItemName}
      updateItem={updateItem}
      renderForm={renderForm}
    />
  );
});


// AppointmentUpdateComponent
const AppointmentUpdateComponent = forwardRef(({ apiService, alertService, appointmentContext }, ref) => {
  const getItemName = () => 'appointment';

  const updateItem = async (updatedItem, dataContext, apiService) => {
    const mutation = `
      mutation UpdateAppointment(
        $id: Int!,
        $department: String,
        $appointmentTime: DateTime,
        $status: String,
        $appointmentType: String,
        $notes: String,
        $duration: Int,
        $priority: String,
        $isRecurring: Boolean,
        $recurrencePattern: String
      ) {
        updateAppointment(
          id: $id,
          department: $department,
          appointmentTime: $appointmentTime,
          status: $status,
          appointmentType: $appointmentType,
          notes: $notes,
          duration: $duration,
          priority: $priority,
          isRecurring: $isRecurring,
          recurrencePattern: $recurrencePattern
        ) {
          id
          patientId
          doctorId
          appointmentTime
          department
          appointmentType
          status
          notes
          duration
          priority
          isRecurring
          recurrencePattern
          patient {
            id
            name
            patientId
          }
          doctor {
            id
            name
          }
          organization { id name }
        }
      }
    `;

    const variables = {
      id: parseInt(updatedItem.id, 10),
      // patientId and doctorId are not included - they cannot be changed
      department: updatedItem.department || null,
      appointmentTime: updatedItem.appointmentTime || null,
      status: updatedItem.status || null,
      appointmentType: updatedItem.appointmentType || null,
      notes: updatedItem.notes || null,
      duration: updatedItem.duration ? parseInt(updatedItem.duration, 10) : null,
      priority: updatedItem.priority || null,
      isRecurring: updatedItem.isRecurring ?? false,
      recurrencePattern: updatedItem.recurrencePattern || null,
    };

    const response = await apiService.graphqlRequest(mutation, variables);
    if (!response || !response.updateAppointment || response.errors) {
      throw new Error(response.errors?.[0]?.message || 'Invalid response from server');
    }

    // Use dataContext.items and dataContext.setItems with safeguard
    const itemsArray = Array.isArray(dataContext.items) ? dataContext.items : [];
    dataContext.setItems(
      itemsArray.map((item) =>
        item.id === updatedItem.id ? response.updateAppointment : item
      )
    );
  };

  const renderForm = ({ selectedItem, setSelectedItem, handleUpdateItem, isLoading, closeModal }) => {
    const validateForm = () => {
      // Patient and Doctor are already assigned and cannot be changed
      return true;
    };

    return (
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 id="update-appointment-title" className="text-xl font-bold text-gray-900">Update Appointment</h3>
          <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" aria-label="Close modal">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Patient and Doctor Information Display */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Assignment:</h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div>
              <span className="font-medium text-gray-600">Patient:</span>
              <div className="text-gray-800">
                {selectedItem?.patient?.name || 'N/A'}
                {selectedItem?.patient?.patientId && (
                  <span className="text-gray-500 ml-2">({selectedItem.patient.patientId})</span>
                )}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Doctor:</span>
              <div className="text-gray-800">
                {selectedItem?.doctor?.name || 'N/A'}
                {selectedItem?.doctor?.id && (
                  <span className="text-gray-500 ml-2">(ID: {selectedItem.doctor.id})</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (validateForm()) handleUpdateItem(selectedItem);
          }}
          className="space-y-4"
        >
          
          <div>
            <label htmlFor="update-appointmentTime" className="block text-sm font-medium text-gray-700">Appointment Time</label>
            <input
              type="datetime-local"
              id="update-appointmentTime"
              value={selectedItem?.appointmentTime ? new Date(selectedItem.appointmentTime).toISOString().slice(0, 16) : ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, appointmentTime: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            />
          </div>
          
          <div>
            <label htmlFor="update-department" className="block text-sm font-medium text-gray-700">Department</label>
            <select
              id="update-department"
              value={selectedItem?.department || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, department: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="">Select Department</option>
              <option value="Cardiology">Cardiology</option>
              <option value="Orthopedics">Orthopedics</option>
              <option value="Emergency">Emergency</option>
              <option value="Pediatrics">Pediatrics</option>
              <option value="General Medicine">General Medicine</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="update-appointmentType" className="block text-sm font-medium text-gray-700">Appointment Type</label>
            <select
              id="update-appointmentType"
              value={selectedItem?.appointmentType || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, appointmentType: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="">Select Type</option>
              <option value="Consultation">Consultation</option>
              <option value="Follow-up">Follow-up</option>
              <option value="Procedure">Procedure</option>
              <option value="general">General</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="update-status" className="block text-sm font-medium text-gray-700">Status</label>
            <select
              id="update-status"
              value={selectedItem?.status || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="update-notes" className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              id="update-notes"
              value={selectedItem?.notes || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, notes: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              rows="3"
              placeholder="Add appointment notes..."
            />
          </div>
          
          <div>
            <label htmlFor="update-duration" className="block text-sm font-medium text-gray-700">Duration (minutes)</label>
            <input
              type="number"
              id="update-duration"
              value={selectedItem?.duration || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, duration: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              placeholder="Duration in minutes"
              min="1"
              max="480"
            />
          </div>
          
          <div>
            <label htmlFor="update-priority" className="block text-sm font-medium text-gray-700">Priority</label>
            <select
              id="update-priority"
              value={selectedItem?.priority || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, priority: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
            >
              <option value="">Select Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="update-isRecurring"
              checked={selectedItem?.isRecurring || false}
              onChange={(e) => setSelectedItem({ ...selectedItem, isRecurring: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="update-isRecurring" className="text-sm font-medium text-gray-700">
              Is Recurring Appointment?
            </label>
          </div>
          
          {selectedItem?.isRecurring && (
            <div>
              <label htmlFor="update-recurrencePattern" className="block text-sm font-medium text-gray-700">Recurrence Pattern</label>
              <input
                type="text"
                id="update-recurrencePattern"
                value={selectedItem?.recurrencePattern || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, recurrencePattern: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                placeholder="e.g., weekly, monthly, etc."
              />
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={closeModal}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Saving...' : 'Update Appointment'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <UpdateComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={appointmentContext}
      getItemName={getItemName}
      updateItem={updateItem}
      renderForm={renderForm}
    />
  );
});



// Fixed StaffUpdateComponent
const StaffUpdateComponent = forwardRef(({ apiService, alertService, staffContext }, ref) => {
    const getItemName = () => 'staff';

    const updateItem = async (updatedItem, dataContext, apiService) => {
        const mutation = `
            mutation UpdateStaff($staffId: Int!, $name: String, $specialization: String, $department: String, $status: String, $phone: String, $email: String, $hireDate: DateTime, $salary: Float, $workSchedule: String) {
                updateStaff(staffId: $staffId, name: $name, specialization: $specialization, department: $department, status: $status, phone: $phone, email: $email, hireDate: $hireDate, salary: $salary, workSchedule: $workSchedule) {
                    id
                    name
                    specialization
                    department
                    phone
                    email
                    hireDate
                    salary
                    workSchedule
                    status
                    organization { id name }
                }
            }
        `;
        const variables = {
            staffId: parseInt(updatedItem.id, 10),
            name: updatedItem.name || null,
            specialization: updatedItem.specialization || null,
            department: updatedItem.department || null,
            status: updatedItem.status || null,
            phone: updatedItem.phone || null,
            email: updatedItem.email || null,
            hireDate: updatedItem.hireDate || null,
            salary: updatedItem.salary ? parseFloat(updatedItem.salary) : null,
            workSchedule: updatedItem.workSchedule || null,
        };
        const response = await apiService.graphqlRequest(mutation, variables);
        if (!response || !response.updateStaff || response.errors) {
            throw new Error(response.errors?.[0]?.message || 'Invalid response from server');
        }

        // Use dataContext.items and dataContext.setItems with safeguard
        const itemsArray = Array.isArray(dataContext.items) ? dataContext.items : [];
        dataContext.setItems(
            itemsArray.map((item) =>
                item.id === updatedItem.id ? response.updateStaff : item
            )
        );
    };

    const renderForm = ({ selectedItem, setSelectedItem, handleUpdateItem, isLoading, closeModal }) => {
        const validateForm = () => {
            if (!selectedItem?.name || !selectedItem?.role) {
                alertService.showAlert('Name and Role are required', 'error');
                return false;
            }
            if (selectedItem?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedItem.email)) {
                alertService.showAlert('Please enter a valid email address', 'error');
                return false;
            }
            return true;
        };

        const formatSalary = (salary) => {
            if (!salary) return 'Not set';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(salary);
        };

        const formatDate = (dateString) => {
            if (!dateString) return 'Not set';
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        };

        return (
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 id="update-staff-title" className="text-xl font-bold text-gray-900">Update Staff Member</h3>
                    <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" aria-label="Close modal">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Current Staff Information Display */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Current Staff Information:</h4>
                    <div className="grid grid-cols-1 gap-3 text-sm">
                        <div>
                            <span className="font-medium text-gray-600">Name:</span>
                            <div className="text-gray-800 font-semibold">{selectedItem?.name || 'N/A'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-600">Role:</span>
                            <div className="text-gray-800">{selectedItem?.role || 'N/A'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-600">Department:</span>
                            <div className="text-gray-800">{selectedItem?.department || 'Not assigned'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-600">Status:</span>
                            <div className="text-gray-800">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                    selectedItem?.status === 'available' ? 'bg-green-100 text-green-800' :
                                    selectedItem?.status === 'busy' ? 'bg-yellow-100 text-yellow-800' :
                                    selectedItem?.status === 'on-leave' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                    {selectedItem?.status || 'Unknown'}
                                </span>
                            </div>
                        </div>
                        <div>
                            <span className="font-medium text-gray-600">Staff ID:</span>
                            <div className="text-gray-800">{selectedItem?.id || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (validateForm()) handleUpdateItem(selectedItem);
                    }}
                    className="space-y-4"
                >
                    <div>
                        <label htmlFor="update-name" className="block text-sm font-medium text-gray-700">
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="update-name"
                            value={selectedItem?.name || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter full name"
                            required
                        />
                    </div>
                    
                    <div>
                        <label htmlFor="update-role" className="block text-sm font-medium text-gray-700">
                            Role <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="update-role"
                            value={selectedItem?.role || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, role: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        >
                            <option value="">Select Role</option>
                            <option value="Doctor">Doctor</option>
                            <option value="Nurse">Nurse</option>
                            <option value="Administrator">Administrator</option>
                            <option value="Technician">Technician</option>
                        </select>
                    </div>
                    
                    <div>
                        <label htmlFor="update-specialization" className="block text-sm font-medium text-gray-700">Specialization</label>
                        <input
                            type="text"
                            id="update-specialization"
                            value={selectedItem?.specialization || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, specialization: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Cardiology, Pediatrics"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Current: {selectedItem?.specialization || 'Not specified'}
                        </p>
                    </div>
                    
                    <div>
                        <label htmlFor="update-department" className="block text-sm font-medium text-gray-700">Department</label>
                        <select
                            id="update-department"
                            value={selectedItem?.department || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, department: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select Department</option>
                            <option value="Cardiology">Cardiology</option>
                            <option value="Orthopedics">Orthopedics</option>
                            <option value="Emergency">Emergency</option>
                            <option value="Pediatrics">Pediatrics</option>
                            <option value="General Medicine">General Medicine</option>
                            <option value="Administration">Administration</option>
                            <option value="Laboratory">Laboratory</option>
                            <option value="Radiology">Radiology</option>
                        </select>
                    </div>
                    
                    <div>
                        <label htmlFor="update-phone" className="block text-sm font-medium text-gray-700">Phone</label>
                        <input
                            type="tel"
                            id="update-phone"
                            value={selectedItem?.phone || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, phone: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., +1 (555) 123-4567"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Current: {selectedItem?.phone || 'Not provided'}
                        </p>
                    </div>
                    
                    <div>
                        <label htmlFor="update-email" className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            id="update-email"
                            value={selectedItem?.email || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, email: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="name@example.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Current: {selectedItem?.email || 'Not provided'}
                        </p>
                    </div>
                    
                    <div>
                        <label htmlFor="update-hireDate" className="block text-sm font-medium text-gray-700">Hire Date</label>
                        <input
                            type="date"
                            id="update-hireDate"
                            value={selectedItem?.hireDate ? new Date(selectedItem.hireDate).toISOString().slice(0, 10) : ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, hireDate: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Current: {formatDate(selectedItem?.hireDate)}
                        </p>
                    </div>
                    
                    <div>
                        <label htmlFor="update-salary" className="block text-sm font-medium text-gray-700">Salary</label>
                        <input
                            type="number"
                            id="update-salary"
                            value={selectedItem?.salary || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, salary: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Annual salary"
                            min="0"
                            step="1000"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Current: {formatSalary(selectedItem?.salary)}
                        </p>
                    </div>
                    
                    <div>
                        <label htmlFor="update-workSchedule" className="block text-sm font-medium text-gray-700">Work Schedule</label>
                        <input
                            type="text"
                            id="update-workSchedule"
                            value={selectedItem?.workSchedule || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, workSchedule: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Monday-Friday 9AM-5PM"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Current: {selectedItem?.workSchedule || 'Not specified'}
                        </p>
                    </div>
                    
                    <div>
                        <label htmlFor="update-status" className="block text-sm font-medium text-gray-700">Status</label>
                        <select
                            id="update-status"
                            value={selectedItem?.status || ''}
                            onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="available">Available</option>
                            <option value="busy">Busy</option>
                            <option value="on-leave">On Leave</option>
                        </select>
                    </div>
                    
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50 transition-colors"
                        >
                            {isLoading ? 'Updating...' : 'Update Staff'}
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    return (
        <UpdateComponent
            ref={ref}
            apiService={apiService}
            alertService={alertService}
            dataContext={staffContext}
            getItemName={getItemName}
            updateItem={updateItem}
            renderForm={renderForm}
        />
    );
});




const InventoryUpdateComponent = forwardRef(({ apiService, alertService, inventoryContext }, ref) => {
  const getItemName = () => 'inventory item';

  const updateItem = async (updatedItem, dataContext, apiService) => {
    const mutation = `
      mutation UpdateInventoryItem($id: Int!, $name: String, $itemType: String, $quantity: Int, $unit: String, $expiryDate: DateTime, $location: String, $supplier: String, $reorderThreshold: Int, $costPrice: Float, $sellingPrice: Float) {
        updateInventoryItem(id: $id, name: $name, itemType: $itemType, quantity: $quantity, unit: $unit, expiryDate: $expiryDate, location: $location, supplier: $supplier, reorderThreshold: $reorderThreshold, costPrice: $costPrice, sellingPrice: $sellingPrice) {
          id
          name
          itemType
          quantity
          unit
          expiryDate
          location
          supplier
          reorderThreshold
          costPrice
          sellingPrice
          organization { id name }
        }
      }
    `;

    // Parse and validate numeric fields
    const reorderThreshold = updatedItem.reorderThreshold
      ? parseInt(updatedItem.reorderThreshold, 10)
      : null;
    const quantity = updatedItem.quantity ? parseInt(updatedItem.quantity, 10) : null;
    const costPrice = updatedItem.costPrice ? parseFloat(updatedItem.costPrice) : null;
    const sellingPrice = updatedItem.sellingPrice ? parseFloat(updatedItem.sellingPrice) : null;

    // Validate numeric fields
    if (reorderThreshold !== null && isNaN(reorderThreshold)) {
      throw new Error("Reorder Threshold must be a valid integer");
    }
    if (quantity !== null && isNaN(quantity)) {
      throw new Error("Quantity must be a valid integer");
    }
    if (costPrice !== null && isNaN(costPrice)) {
      throw new Error("Cost Price must be a valid number");
    }
    if (sellingPrice !== null && isNaN(sellingPrice)) {
      throw new Error("Selling Price must be a valid number");
    }

    const variables = {
      id: parseInt(updatedItem.id, 10),
      name: updatedItem.name || null,
      itemType: updatedItem.itemType || null,
      quantity,
      unit: updatedItem.unit || null,
      expiryDate: updatedItem.expiryDate || null,
      location: updatedItem.location || null,
      supplier: updatedItem.supplier || null,
      reorderThreshold,
      costPrice,
      sellingPrice,
    };

    const response = await apiService.graphqlRequest(mutation, variables);
    if (!response || !response.updateInventoryItem || response.errors) {
      throw new Error(response.errors?.[0]?.message || 'Invalid response from server');
    }

    // Use dataContext.items and dataContext.setItems with safeguard
    const itemsArray = Array.isArray(dataContext.items) ? dataContext.items : [];
    dataContext.setItems(
      itemsArray.map((item) =>
        item.id === updatedItem.id ? response.updateInventoryItem : item
      )
    );
  };

  const renderForm = ({ selectedItem, setSelectedItem, handleUpdateItem, isLoading, closeModal }) => {
    const validateForm = () => {
      if (!selectedItem?.name || !selectedItem?.itemType) {
        alertService.showAlert('Name and Item Type are required', 'error');
        return false;
      }
      if (selectedItem?.quantity && selectedItem.quantity < 0) {
        alertService.showAlert('Quantity cannot be negative', 'error');
        return false;
      }
      if (selectedItem?.reorderThreshold && selectedItem.reorderThreshold < 0) {
        alertService.showAlert('Reorder Threshold cannot be negative', 'error');
        return false;
      }
      if (selectedItem?.costPrice && selectedItem.costPrice < 0) {
        alertService.showAlert('Cost Price cannot be negative', 'error');
        return false;
      }
      if (selectedItem?.sellingPrice && selectedItem.sellingPrice < 0) {
        alertService.showAlert('Selling Price cannot be negative', 'error');
        return false;
      }
      return true;
    };

    const formatPrice = (price) => {
      if (!price) return 'Not set';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price);
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'Not set';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'in-stock':
          return 'bg-green-100 text-green-800';
        case 'low-stock':
          return 'bg-yellow-100 text-yellow-800';
        case 'out-of-stock':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 id="update-inventory-title" className="text-xl font-bold text-gray-900">Update Inventory Item</h3>
          <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" aria-label="Close modal">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Current Inventory Information Display */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Current Inventory Information:</h4>
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div>
              <span className="font-medium text-gray-600">Item Name:</span>
              <div className="text-gray-800 font-semibold">{selectedItem?.name || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Type:</span>
              <div className="text-gray-800">{selectedItem?.itemType || 'N/A'}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-medium text-gray-600">Quantity:</span>
                <div className="text-gray-800">{selectedItem?.quantity || 0} {selectedItem?.unit || 'units'}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Status:</span>
                <div className="text-gray-800">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedItem?.status)}`}>
                    {selectedItem?.status?.replace('-', ' ') || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Location:</span>
              <div className="text-gray-800">{selectedItem?.location || 'Not specified'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600">Item ID:</span>
              <div className="text-gray-800">{selectedItem?.id || 'N/A'}</div>
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (validateForm()) handleUpdateItem(selectedItem);
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="update-name" className="block text-sm font-medium text-gray-700">
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="update-name"
              value={selectedItem?.name || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter item name"
              required
            />
          </div>

          <div>
            <label htmlFor="update-itemType" className="block text-sm font-medium text-gray-700">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              id="update-itemType"
              value={selectedItem?.itemType || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, itemType: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select Type</option>
              <option value="Medication">Medication</option>
              <option value="Equipment">Equipment</option>
              <option value="Supplies">Supplies</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="update-quantity" className="block text-sm font-medium text-gray-700">Quantity</label>
              <input
                type="number"
                id="update-quantity"
                value={selectedItem?.quantity || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, quantity: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: {selectedItem?.quantity || 0}
              </p>
            </div>
            <div>
              <label htmlFor="update-unit" className="block text-sm font-medium text-gray-700">Unit</label>
              <input
                type="text"
                id="update-unit"
                value={selectedItem?.unit || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, unit: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., boxes, bottles, pieces"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: {selectedItem?.unit || 'Not specified'}
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="update-expiryDate" className="block text-sm font-medium text-gray-700">Expiry Date</label>
            <input
              type="date"
              id="update-expiryDate"
              value={selectedItem?.expiryDate ? new Date(selectedItem.expiryDate).toISOString().slice(0, 10) : ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, expiryDate: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Current: {formatDate(selectedItem?.expiryDate)}
            </p>
          </div>

          <div>
            <label htmlFor="update-location" className="block text-sm font-medium text-gray-700">Location</label>
            <input
              type="text"
              id="update-location"
              value={selectedItem?.location || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, location: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Storage Room A, Pharmacy"
            />
            <p className="text-xs text-gray-500 mt-1">
              Current: {selectedItem?.location || 'Not specified'}
            </p>
          </div>

          <div>
            <label htmlFor="update-supplier" className="block text-sm font-medium text-gray-700">Supplier</label>
            <input
              type="text"
              id="update-supplier"
              value={selectedItem?.supplier || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, supplier: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter supplier name"
            />
            <p className="text-xs text-gray-500 mt-1">
              Current: {selectedItem?.supplier || 'Not specified'}
            </p>
          </div>

          <div>
            <label htmlFor="update-reorderThreshold" className="block text-sm font-medium text-gray-700">Reorder Threshold</label>
            <input
              type="number"
              id="update-reorderThreshold"
              value={selectedItem?.reorderThreshold || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, reorderThreshold: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Minimum quantity before reorder"
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Current: {selectedItem?.reorderThreshold || 'Not set'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="update-costPrice" className="block text-sm font-medium text-gray-700">Cost Price</label>
              <input
                type="number"
                id="update-costPrice"
                step="0.01"
                value={selectedItem?.costPrice || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, costPrice: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: {formatPrice(selectedItem?.costPrice)}
              </p>
            </div>
            <div>
              <label htmlFor="update-sellingPrice" className="block text-sm font-medium text-gray-700">Selling Price</label>
              <input
                type="number"
                id="update-sellingPrice"
                step="0.01"
                value={selectedItem?.sellingPrice || ''}
                onChange={(e) => setSelectedItem({ ...selectedItem, sellingPrice: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Current: {formatPrice(selectedItem?.sellingPrice)}
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="update-status" className="block text-sm font-medium text-gray-700">Status</label>
            <select
              id="update-status"
              value={selectedItem?.status || ''}
              onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="in-stock">In Stock</option>
              <option value="low-stock">Low Stock</option>
              <option value="out-of-stock">Out of Stock</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={closeModal}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Updating...' : 'Update Item'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <UpdateComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={inventoryContext}
      getItemName={getItemName}
      updateItem={updateItem}
      renderForm={renderForm}
    />
  );
});


export default InventoryUpdateComponent;

export { UpdateComponent, PatientUpdateComponent, AppointmentUpdateComponent, StaffUpdateComponent, InventoryUpdateComponent };